import { useCallback } from 'react';
import { Typography } from '@mui/material';
import { products } from '@terraframing-suite/product-config';
import LocalDate from '../../components/LocalDate';
import { useGrowers } from '../../hooks/use-growers';
import { usePermission } from '../../hooks/use-permission';

const historicalEnabledLayers = [
  'potassium-critical',
  'potassium-delta-critical',
  'potassium-removal',
  'potassium',
  'phosphorus',
  'phosphorus-critical',
  'phosphorus-delta-critical',
  'phosphorus-removal',
  'potential-productivity',
  'nitrogen-loss',
  'swi',
  'slope',
  'om',
  'cec',
  'buffer-ph',
  'ph',
  'boundary',
];

export function useLayers() {
  const { selected: grower } = useGrowers();
  const canViewSignal = usePermission('VIEW_SIGNAL');

  const layers = useCallback(
    (supportingLayers) => {
      const enabledLayers = supportingLayers.filter((layer) =>
        historicalEnabledLayers.includes(layer.id),
      );

      const efficacyLayers =
        grower?.organization?.enabledProducts?.map((product) => ({
          featureId: 'efficacy',
          id: `${product}-efficacy`,
          name: products.find((p) => p.id === product)?.label,
          filters: { product },
          skipLabels: true,
          legendSort: 'desc',
        })) || [];

      return [
        ...enabledLayers.slice(0, -1),
        ...(canViewSignal ? efficacyLayers : []),
        ...enabledLayers.slice(-1),
      ].map((layer) => ({ ...layer, buildLayerLabel }));
    },
    [canViewSignal, grower],
  );

  return { layers };
}

function buildLayerSubLabel(meta = {}) {
  const { createdAt, startsAt, endsAt } = meta;

  if (startsAt)
    return (
      <>
        <LocalDate value={startsAt} timeZone="UTC" />
        {' - '}
        {endsAt ? <LocalDate value={endsAt} timeZone="UTC" /> : 'Current'}
      </>
    );
  if (createdAt) return <LocalDate value={createdAt} />;
}

function buildLayerLabel(layer) {
  const subLabel = buildLayerSubLabel(layer?.meta);

  if (!subLabel) return layer.name;

  return (
    <>
      {layer.name}
      <Typography sx={{ fontSize: '.8rem !important' }}>{subLabel}</Typography>
    </>
  );
}
